"use client"

import {useEffect, useState} from "react"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Plus } from "lucide-react"
import { createProject, updateProject } from "@/services/project-service"
import { toast } from "sonner"
import { Project } from "@/lib/types"
import { useTranslation } from "react-i18next";

interface CreateProjectDialogProps {
  onProjectCreated: (project: Project) => void
  trigger?: React.ReactNode
  project?: Project // Optional project for edit mode
  mode?: 'create' | 'edit'
}

export function CreateProjectDialog({ onProjectCreated, trigger, project, mode = 'create' }: CreateProjectDialogProps) {
  const [open, setOpen] = useState(false)
  const [isCreating, setIsCreating] = useState(false)
  const [name, setName] = useState("")
  const [description, setDescription] = useState("")
  const [imageUrl, setImageUrl] = useState("")

  const isEditMode = mode === 'edit' && project
  const { t, i18n } = useTranslation("common");

  // Reset form when project changes or modal opens
  useEffect(() => {
    if (isEditMode) {
      setName(project.name)
      setDescription(project.description || "")
      setImageUrl(project.image_url || "")
    } else {
      setName("")
      setDescription("")
      setImageUrl("")
    }
  }, [isEditMode, project])

  const handleCreateOrUpdate = async () => {
    if (!name.trim()) {
      toast.error(t("common:Project-dialog.toast.nameRequired"));
      return
    }

    setIsCreating(true)
    try {
      let result
      
      if (isEditMode) {
        result = await updateProject(project.id, {
          name: name.trim(),
          description: description.trim() || undefined,
          image_url: imageUrl.trim() || undefined
        })
      } else {
        result = await createProject(name.trim(), description.trim() || undefined, imageUrl.trim() || undefined)
      }
      
      if (!result.success) {
        throw new Error(result.error || `Failed to ${isEditMode ? 'update' : 'create'} project`)
      }

      toast.success(
        t(`common:Project-dialog.toast.${isEditMode ? "updated" : "created"}`)
      );
      onProjectCreated(result.project!)
      setOpen(false)
      
      // Reset form only if creating
      if (!isEditMode) {
        setName("")
        setDescription("")
        setImageUrl("")
      }
    } catch (error) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} project:`, error)
      toast.error(
        t(
          `common:Project-dialog.toast.not${isEditMode ? "Updated" : "Created"}`
        )
      );
    } finally {
      setIsCreating(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button>
            <Plus className="mr-2 h-4 w-4" />
            {t("common:Project-dialog.new")}
          </Button>
        )}
      </DialogTrigger>
      <DialogContent
        className={i18n.language === "ar" ? "rtl" : ""}
        style={{ direction: i18n.language === "ar" ? "rtl" : "ltr" }}
      >
        <DialogHeader>
          <DialogTitle
            className={i18n.language === "ar" ? "text-right" : "text-left"}
          >
            {isEditMode
              ? t("common:Project-dialog.titleUpdate")
              : t("common:Project-dialog.titleCreate")}
          </DialogTitle>
          <DialogDescription
            className={i18n.language === "ar" ? "text-right" : "text-left"}
          >
            {isEditMode
              ? t("common:Project-dialog.descriptionUpdate")
              : t("common:Project-dialog.descriptionCreate")}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="name">{t("common:Project-dialog.name")}</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t("common:Project-dialog.namePlaceholder")}
              dir={i18n.language === "ar" ? "rtl" : "ltr"}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="description">
              {t("common:Project-dialog.description")}
            </Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t("common:Project-dialog.descriptionPlaceholder")}
              rows={3}
              dir={i18n.language === "ar" ? "rtl" : "ltr"}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="imageUrl">{t("common:Project-dialog.image")}</Label>
            <Input
              id="imageUrl"
              value={imageUrl}
              onChange={(e) => setImageUrl(e.target.value)}
              placeholder={t("common:Project-dialog.imagePlaceholder")}
              dir={i18n.language === "ar" ? "rtl" : "ltr"}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            {t("common:buttons.cancel")}
          </Button>
          <Button
            onClick={handleCreateOrUpdate}
            disabled={isCreating || !name.trim()}
          >
            {isCreating
              ? isEditMode
                ? t("common:buttons.updating")
                : t("common:buttons.creating")
              : isEditMode
              ? t("common:Project-dialog.titleUpdate")
              : t("common:Project-dialog.createButton")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
